/**
 * Independence questionnaire: resolve DOC_LIBRARY (FY_YEAR) for UI substitution.
 * Reads browser storage directly so production/Railway does not depend on React context timing.
 */

export const INDEPENDENCE_ENTITY_PLACEHOLDER = "[Entity Name and Code]";

const PLACEHOLDER_PATTERN = /\[Entity\s+Name\s+and\s+Code\]/gi;

const WEB_ARCHIVE_DOC_LIBRARY_KEY = "web_archive_doc_library";
const WEB_ARCHIVE_FY_YEAR_KEY = "web_archive_fy_year";

export function isIndependencePlaceholderValue(value: string | undefined | null): boolean {
  if (!value?.trim()) return false;
  return PLACEHOLDER_PATTERN.test(value.trim());
}

/** Persist labels when offline ZIP is loaded (survives context/remount quirks). */
export function persistWebArchiveEntityLabels(docLibrary: string, fyYear: string): void {
  if (typeof sessionStorage === "undefined") return;
  const lib = docLibrary.trim();
  const fy = fyYear.trim();
  if (lib && !isIndependencePlaceholderValue(lib)) {
    sessionStorage.setItem(WEB_ARCHIVE_DOC_LIBRARY_KEY, lib);
  }
  if (fy && !isIndependencePlaceholderValue(fy)) {
    sessionStorage.setItem(WEB_ARCHIVE_FY_YEAR_KEY, fy);
  }
}

function pickLabelPart(...candidates: Array<string | undefined | null>): string {
  for (const c of candidates) {
    const v = c?.trim() ?? "";
    if (v && !isIndependencePlaceholderValue(v)) return v;
  }
  return "";
}

/** Resolve `Client Name (FY)` from sharepoint_config, session backup, and engagement. */
export function resolveIndependenceEntityLabel(): string {
  if (typeof window === "undefined") return "— (—)";

  let lib = "";
  let fy = "";

  try {
    const raw = localStorage.getItem("sharepoint_config");
    if (raw) {
      const c = JSON.parse(raw) as Record<string, unknown>;
      lib = pickLabelPart(String(c.DOC_LIBRARY ?? ""));
      fy = pickLabelPart(String(c.FY_YEAR ?? ""));
    }
  } catch {
    /* ignore */
  }

  if (typeof sessionStorage !== "undefined") {
    lib =
      lib ||
      pickLabelPart(sessionStorage.getItem(WEB_ARCHIVE_DOC_LIBRARY_KEY));
    fy =
      fy || pickLabelPart(sessionStorage.getItem(WEB_ARCHIVE_FY_YEAR_KEY));
  }

  try {
    const engId = localStorage.getItem("current_engagement_id");
    const rawEng = localStorage.getItem("juggernaut_engagements");
    if (engId && rawEng) {
      const list = JSON.parse(rawEng) as Array<{
        id?: string;
        clientName?: string;
        engagementName?: string;
        financialYear?: string;
      }>;
      const eng = Array.isArray(list)
        ? list.find((e) => e?.id === engId)
        : undefined;
      if (eng) {
        lib = lib || pickLabelPart(eng.clientName);
        fy =
          fy ||
          pickLabelPart(eng.engagementName, eng.financialYear);
      }
    }
  } catch {
    /* ignore */
  }

  return `${lib || "—"} (${fy || "—"})`;
}

/** Replace every `[Entity Name and Code]` in questionnaire copy. */
export function substituteIndependenceEntityInText(text: string): string {
  const label = resolveIndependenceEntityLabel();
  return text.replace(PLACEHOLDER_PATTERN, label);
}
